import { MouseEvent } from 'react'
import styled from '@emotion/styled'
import * as RadixToast from '@radix-ui/react-toast'
import { CircularProgress } from '@mui/material'
import { X, Check, CircleX } from 'lucide-react'
import Typography from 'components/core/Typography'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { ToasterToast } from 'hooks/useToast'

// @TODO: make side panel headers Sentence case

const StyledToastRoot = styled(RadixToast.Root)<{ isError: boolean }>`
    min-height: 6.8rem;
    position: relative;

    background-color: ${(p) =>
        p.isError
            ? DESIGN_TOKENS.background.error
            : DESIGN_TOKENS.background.primary};
    border: 0.1rem solid
        ${(p) =>
            p.isError
                ? DESIGN_TOKENS.background.error
                : DESIGN_TOKENS.border.input};
    border-radius: 0.6rem;
    box-shadow: ${DESIGN_TOKENS.boxShadow.toast};

    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding-left: 2.4rem;
    padding-right: 2.4rem;
    column-gap: 1.2rem;

    .toast__icon {
        display: flex;
        align-items: center;
        color: ${DESIGN_TOKENS.text.info};
    }

    .toast__action {
        position: absolute;
        top: 0;
        right: 0;
        padding-top: 0.6rem;
        padding-right: 0.6rem;
        background-color: transparent;

        .toast__action__button {
            margin: 0;
            padding: 0.4rem;

            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 0.4rem;
            background-color: transparent;
            border: none;
            cursor: pointer;

            opacity: 0;

            color: ${DESIGN_TOKENS.text.mutedForeground};
            transition: ${DESIGN_TOKENS.transition.inputHover};

            &:hover {
                color: ${DESIGN_TOKENS.text.foreground};
            }

            .toast__action__button__icon {
                color: inherit;
            }
        }
    }

    &:hover .toast__action__button {
        opacity: 1;
    }

    &[data-state='open'] {
        animation: slideIn 150ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    &[data-state='closed'] {
        animation: slideOut 100ms ease-out;
    }

    @keyframes slideIn {
        from {
            transform: translateY(-100%);
        }
        to {
            transform: translateY(0);
        }
    }

    @keyframes slideOut {
        from {
            transform: translateX(var(--radix-toast-swipe-end-x));
        }
        to {
            transform: translateX(calc(100% + 2.4rem));
        }
    }
`

export const Toast = ({
    open,
    variant,
    description,
    onClose,
    onOpenChange,
    ...props
}: ToasterToast) => {
    const _onClose = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        onClose()
    }

    return (
        <StyledToastRoot
            {...props}
            isError={variant === 'error'}
            className="ToastRoot"
            open={open}
            onOpenChange={onOpenChange}
        >
            <div className="toast__icon">
                {variant === 'info' && (
                    <CircularProgress size={24} color="inherit" />
                )}
                {variant === 'success' && (
                    <Check size={24} color={DESIGN_TOKENS.text.success} />
                )}
                {variant === 'error' && (
                    <CircleX size={24} color={DESIGN_TOKENS.text.foreground} />
                )}
            </div>
            <RadixToast.Description asChild>
                <Typography color="primary" variant="body1">
                    {description}
                </Typography>
            </RadixToast.Description>
            <div className="toast__action">
                <RadixToast.Close asChild>
                    <button
                        className="toast__action__button"
                        onClick={_onClose}
                    >
                        <X size={16} className="toast__action__button__icon" />
                    </button>
                </RadixToast.Close>
            </div>
        </StyledToastRoot>
    )
}
